/**
 * Main Application Class
 * Centralized initialization and management of all components
 */
// Layout Components
import { Header } from "./components/layout/Header.js";
import { Footer } from "./components/layout/Footer.js";
import { SubNav } from "./components/layout/SubNav.js";
import { SubHeroAnim } from "./components/layout/SubHeroAnim.js";
import { GoToTop } from "./components/layout/GoToTop.js";
// Page Components
import { Hero } from "./components/page/Hero.js";
import { GlassHero } from "./components/page/GlassHero.js";
import { initTextAnim } from "./components/page/TextAnim.js";
import { Marquee } from "./components/page/Marquee.js";
import { Team } from "./components/page/Team.js";
import { Captcha } from "./components/page/Captcha.js";
import { Popup } from "./components/page/Popup.js";
import { Pagination } from "./components/page/Pagination.js";
import { FloatMenu } from "./components/page/FloatMenu.js";
import { SwiperComponents } from "./components/page/Swiper.js";
import { MainMedia } from "./components/page/MainMedia.js";
import { PipelineTable } from "./components/page/PipelineTable.js";
import { MfCursor } from "./components/MfCursor.js";
import { ImageCompareViewer } from "./components/ImageCompareViewer.js";

// Modal Components
import { LanguageModal } from "./components/modal/LanguageModal.js";
import { PrivacyModal } from "./components/modal/PrivacyModal.js";
import { QuickFormModal } from "./components/modal/QuickFormModal.js";
import { QuickForm } from "./components/modal/QuickForm.js";

// UI Components
import { Select } from "./components/ui/Select.js";
import { Modal, ModalContent } from "./components/ui/Modal.js";
import { TeamProfileModal } from "./components/TeamProfileModal.js";
import { initContactForms } from "./components/form/Form.js";

// Utils
import { LenisScroll } from "./utils/Lenis.js";
import { ProfileDropdown } from "./profile-dropdown.js";
import { LanguageDropdown } from "./language-dropdown.js";
import { Context } from "./utils/Context.js";
import { serviceContainer } from "./utils/ServiceContainer.js";
import {
    initScrollImageAnimation,
    initListAnimation,
} from "./utils/GsapAnimations.js";
import { initImageRevealParallax, initCardMaskReveal } from "./utils/ImageRevealParallax.js";
import { initSubPageTextAnim } from "./utils/SubPageTextAnim.js";
import { initAchievementDetailModal } from "./components/AchievementDetailModal.js";
import { initPlatformTechHub } from "./components/PlatformTechHub.js";

// Controllers
import { HomeController } from "./controllers/HomeController.js";

export class Application {
    static instance = null;

    constructor() {
        Application.instance = this;
        this.components = {
            hero: new Hero(),
            glassHero: new GlassHero(),
            header: new Header(),
            languageModal: new LanguageModal(),
            privacyModal: new PrivacyModal(),
            floatMenu: new FloatMenu(),
            goToTop: new GoToTop(),
            captcha: new Captcha(),
            marquee: new Marquee(),
            swiper: new SwiperComponents(),
            footer: new Footer(),
            team: new Team(),
            subNav: new SubNav(),
            quickForm: new QuickForm(),
            quickFormModal: new QuickFormModal(),
            popup: new Popup(),
            pagination: new Pagination(),
            mainMedia: new MainMedia(),
            teamProfileModal: new TeamProfileModal(),
            pipelineTable: new PipelineTable(),
            mfCursor: new MfCursor(),
            subHeroAnim: new SubHeroAnim(),
            imageCompareViewer: new ImageCompareViewer(),
        };
        this.utils = {
            lenis: new LenisScroll(),
        };

        // Controllers registry
        this.controllers = {
            home: new HomeController(),
        };

        // Current page controller instance
        this.pageController = null;
    }

    init() {
        console.log("Application initialized");

        document.addEventListener("DOMContentLoaded", () => {
            // Initialize utilities first
            this.utils.lenis.init();

            // Initialize all components
            Object.values(this.components).forEach((component) => {
                if (component && typeof component.init === "function") {
                    try {
                        component.init();
                    } catch (error) {
                        console.error(
                            `Error initializing ${component.constructor.name}:`,
                            error,
                        );
                    }
                }
            });

            // VideoSoundToggle을 ServiceContainer에 등록 (이미 constructor에서 등록되지만 확인)
            if (this.components.videoSoundToggle) {
                serviceContainer.share(
                    "videoSoundToggle",
                    this.components.videoSoundToggle,
                );
            }

            // Initialize ProfileDropdown if button exists (header)
            const profileButton = document.getElementById("profileDropdownBtn");
            if (profileButton) {
                this.profileDropdown = new ProfileDropdown(
                    "profileDropdownBtn",
                    "profileDropdown",
                );
            }

            // Initialize ProfileDropdown for drawer if button exists
            const drawerProfileButton = document.getElementById(
                "drawerProfileDropdownBtn",
            );
            if (drawerProfileButton) {
                this.drawerProfileDropdown = new ProfileDropdown(
                    "drawerProfileDropdownBtn",
                    "drawerProfileDropdown",
                );
            }

            // Initialize LanguageDropdown (header, KO/EN only)
            const languageDropdownBtn = document.getElementById(
                "languageDropdownBtn",
            );
            if (languageDropdownBtn) {
                this.languageDropdown = new LanguageDropdown(
                    "languageDropdownBtn",
                    "languageDropdown",
                );
            }

            // Initialize LanguageDropdown for drawer (KO/EN only)
            const drawerLanguageDropdownBtn = document.getElementById(
                "drawerLanguageDropdownBtn",
            );
            if (drawerLanguageDropdownBtn) {
                this.drawerLanguageDropdown = new LanguageDropdown(
                    "drawerLanguageDropdownBtn",
                    "drawerLanguageDropdown",
                );
            }

            // Initialize Custom Select components
            this.initSelects();

            // Initialize AOS if available
            if (typeof AOS !== "undefined") {
                AOS.init();
            }

            // Modal과 ModalContent를 전역으로 사용 가능하도록 설정
            window.Modal = Modal;
            window.ModalContent = ModalContent;

            // Context 초기화 (다국어 데이터 로드)
            const ctx = Context.getInstance();
            ctx.init().catch((err) => {
                console.warn("Context initialization failed:", err);
            });

            // Contact form (nb_request API submit + captcha)
            initContactForms();

            // 공통 애니메이션 초기화 (모든 페이지에서 자동 실행)
            initScrollImageAnimation();
            initListAnimation();

            // 서브 히어로 GSAP 텍스트/요소 애니메이션 (AOS 대신)
            if (this.components.subHeroAnim) this.components.subHeroAnim.init();

            // data-mask-image: 클립 리빌 + 패럴랙스 (.main-glass-hero 제외)
            initImageRevealParallax();
            // data-mask-reveal: 카드·리스트 아이템 morph 스케일 등장 (swiper 제외, GSAP ScrollTrigger)
            initCardMaskReveal();
            initAchievementDetailModal();
            initPlatformTechHub();

            // 서브페이지 공통 텍스트 등장 (data-sub-text-anim, AOS 미사용)
            initSubPageTextAnim();

            // 텍스트 애니메이션 (전역 data-text-anim: chars / slide-up)
            initTextAnim();

            // 그라디언트 배경: 스크롤에 따라 좌/상 이동 (--scroll-y, --scroll-x)
            this.syncGradientScroll();

            // 모바일 비디오 재생 보장
            this.initMobileVideoPlayback();

            // 연예인 모달 초기화 (모든 페이지에서 사용 가능)
            this.initCelebrityModal();

            // Page-specific controller 초기화
            this.initPageController();
        });
    }

    /**
     * 그라디언트 배경: 스크롤 시 --scroll-y, --scroll-x 갱신 (좌/상 이동)
     */
    syncGradientScroll() {
        const el = document.querySelector(".gradient-bg");
        if (!el) return;
        const setScroll = () => {
            el.style.setProperty("--scroll-y", String(window.scrollY));
            el.style.setProperty("--scroll-x", String(window.scrollX));
        };
        setScroll();
        window.addEventListener("scroll", setScroll, { passive: true });
    }

    /**
     * 모바일에서 비디오 재생 보장
     */
    initMobileVideoPlayback() {
        const videos = document.querySelectorAll("video[autoplay]");
        if (videos.length === 0) return;

        // 각 비디오에 대해 재생 시도
        videos.forEach((video) => {
            // VideoSoundToggle이 관리하는 비디오는 건너뛰기 (중복 처리 방지)
            const container = video.closest("[data-video-sound]");
            if (container) {
                return; // VideoSoundToggle이 처리함
            }

            // 비디오가 로드되면 재생 시도
            const tryPlay = () => {
                if (video.readyState >= 2) {
                    // 비디오가 로드되었으면 재생 시도
                    video.play().catch((error) => {
                        console.warn(
                            "Video autoplay failed, will retry on interaction:",
                            error,
                        );
                        // 사용자 상호작용 시 재생 시도
                        const playOnInteraction = () => {
                            video.play().catch(() => {});
                            document.removeEventListener(
                                "touchstart",
                                playOnInteraction,
                            );
                            document.removeEventListener(
                                "click",
                                playOnInteraction,
                            );
                        };
                        document.addEventListener(
                            "touchstart",
                            playOnInteraction,
                            { once: true },
                        );
                        document.addEventListener("click", playOnInteraction, {
                            once: true,
                        });
                    });
                }
            };

            // 비디오가 이미 로드된 경우
            if (video.readyState >= 2) {
                tryPlay();
            } else {
                // 비디오 로드 대기
                video.addEventListener("loadeddata", tryPlay, { once: true });
                video.addEventListener("canplay", tryPlay, { once: true });
            }

            // Intersection Observer로 뷰포트에 들어올 때 재생 보장
            const observer = new IntersectionObserver(
                (entries) => {
                    entries.forEach((entry) => {
                        if (entry.isIntersecting) {
                            video.play().catch((error) => {
                                console.warn(
                                    "Video play on intersection failed:",
                                    error,
                                );
                            });
                        }
                    });
                },
                { threshold: 0.1 },
            );

            observer.observe(video);
        });
    }

    /**
     * 연예인 모달 초기화 (모든 페이지에서 사용)
     */
    initCelebrityModal() {
        // 연예인 아이템이 있는 경우에만 초기화 (Marquee 버전 + Swiper 버전)
        const hasCelebrityItems =
            document.querySelectorAll(
                ".no-main-celebrity__item[data-celebrity-id], .no-main-celebrity-swiper__item[data-celebrity-id], .no-celebrity__item[data-celebrity-id]",
            ).length > 0;
        if (hasCelebrityItems) {
            try {
                this.celebrityList = new CelebrityList();
            } catch (error) {
                console.error("Error initializing CelebrityList:", error);
            }
        }
    }

    /**
     * 현재 페이지의 Controller와 Action을 읽어서 해당 메서드 실행
     */
    initPageController() {
        const body = document.body;
        if (!body) return;

        const controllerName = body.getAttribute("data-controller");
        const actionName = body.getAttribute("data-action");

        if (!controllerName || !actionName) {
            return;
        }

        // Controller 찾기
        const Controller = this.controllers[controllerName];
        if (!Controller) {
            console.warn(`Controller not found: ${controllerName}`);
            return;
        }

        // Action 메서드 확인
        if (typeof Controller[actionName] !== "function") {
            console.warn(`Action not found: ${controllerName}.${actionName}`);
            return;
        }

        // Action 실행
        try {
            this.pageController = Controller[actionName]();
            // 반환된 인스턴스가 init 메서드를 가지고 있으면 호출
            if (
                this.pageController &&
                typeof this.pageController.init === "function"
            ) {
                this.pageController.init();
            }
        } catch (error) {
            console.error(
                `Error initializing ${controllerName}.${actionName}:`,
                error,
            );
        }
    }

    initSelects() {
        const selectElements = document.querySelectorAll("[data-select]");
        this.selects = Array.from(selectElements).map((el) => new Select(el));
    }

    getComponent(name) {
        return this.components[name] || null;
    }

    getUtil(name) {
        return this.utils[name] || null;
    }

    static getInstance() {
        return Application.instance;
    }
}
